import { Vec3, vec3, add, sub, scale, cross, dot, length, ZERO } from '../math/vec3'
import { Mat3, mulVec3 } from '../math/mat3'
import { rotate, fromAxisAngle, mulQuat, normalizeQuat } from '../math/quat'
import { ALLUMINIO, Materiale, Shape } from '../vessel/part'
import { Vessel, PartInstance, partById, ancestorsOf, subtreeOf } from '../vessel/vessel'
import { PartCatalog, partDef } from '../vessel/catalog'
import { MassProperties } from '../vessel/massProperties'
import { shapeHalfHeight, shapeRadius } from '../vessel/extents'

/** Costante di tempo con cui la deformazione raggiunge l'equilibrio. */
const TAU = 0.08

export interface Giunto {
  /** id della parte a valle: e li che si spezza */
  readonly id: string
  readonly parentId: string
  /** posizione del giunto nel sistema del veicolo */
  readonly position: Vec3
  /** rigidezza flessionale, N·m/rad */
  readonly stiffness: number
  /** momento che lo rompe, N·m */
  readonly maxMoment: number
  readonly length: number
}

/** Momento d'inerzia di sezione di un guscio cilindrico. */
export function momentoDiSezione(raggio: number, spessore: number): number {
  const D = 2 * raggio
  const d = Math.max(0, D - 2 * spessore)
  return (Math.PI * (D ** 4 - d ** 4)) / 64
}

/**
 * Momento che manda in crisi la sezione.
 *
 * NON e lo snervamento: un guscio sottile cede per IMBOZZAMENTO molto prima.
 * La tensione critica classica e 0.6·E·t/R, ma le imperfezioni reali la
 * abbassano di un fattore quattro — il "knockdown" che ogni manuale di
 * strutture spaziali applica. Usare lo snervamento darebbe limiti mille volte
 * troppo alti, e nessun razzo si spezzerebbe mai.
 */
export function momentoCritico(raggio: number, materiale: Materiale): number {
  const sigmaCritica = 0.15 * materiale.modulus * (materiale.wallThickness / raggio)
  return (Math.min(sigmaCritica, materiale.yieldStrength) * momentoDiSezione(raggio, materiale.wallThickness)) / raggio
}

const raggioStrutturale = (shape: Shape): number => Math.max(0.05, shapeRadius(shape))

/** I giunti strutturali: uno per ogni collegamento assiale fra due parti. */
export function giuntiDi(vessel: Vessel, catalog: PartCatalog): Giunto[] {
  const out: Giunto[] = []
  for (const p of vessel.parts) {
    if (p.parentId === null) continue
    const padre = partById(vessel, p.parentId)
    const defP = partDef(catalog, p.defId)
    const defPadre = partDef(catalog, padre.defId)

    // solo collegamenti in linea: le parti radiali non fanno da trave
    const scostamento = Math.hypot(p.position.x - padre.position.x, p.position.z - padre.position.z)
    if (scostamento > 0.2) continue

    const materiale = defP.material ?? ALLUMINIO
    const raggio = Math.min(raggioStrutturale(defP.shape), raggioStrutturale(defPadre.shape))
    const lunghezza = Math.max(0.2, shapeHalfHeight(defP.shape) + shapeHalfHeight(defPadre.shape))
    const I = momentoDiSezione(raggio, materiale.wallThickness)

    out.push({
      id: p.id,
      parentId: padre.id,
      position: vec3(p.position.x, (p.position.y + padre.position.y) / 2, p.position.z),
      stiffness: (materiale.modulus * I) / lunghezza,
      maxMoment: momentoCritico(raggio, materiale),
      length: lunghezza,
    })
  }
  return out
}

export interface ForzaSuParte {
  readonly partId: string
  readonly force: Vec3
  readonly point: Vec3
}

/**
 * Momento flettente in ogni giunto.
 *
 * Si guarda la porzione di razzo a VALLE del giunto e si sommano i momenti
 * delle forze esterne meno quelli delle forze d'inerzia. Il termine d'inerzia
 * non e un dettaglio: e la ragione per cui un razzo si spezza sotto la propria
 * accelerazione, non solo sotto il vento.
 */
export function momentiFlettenti(
  vessel: Vessel,
  catalog: PartCatalog,
  forze: readonly ForzaSuParte[],
  massProps: MassProperties,
  accelerazione: Vec3,
  alfa: Vec3,
  omega: Vec3,
  giunti: readonly Giunto[],
): Map<string, Vec3> {
  const perParte = new Map<string, Vec3>()
  for (const f of forze) {
    perParte.set(f.partId, add(perParte.get(f.partId) ?? ZERO, f.force))
  }

  const out = new Map<string, Vec3>()
  for (const g of giunti) {
    let momento: Vec3 = ZERO
    for (const p of subtreeOf(vessel, g.id)) {
      const def = partDef(catalog, p.defId)
      const massa = def.dryMass + p.propellant
      const d = sub(p.position, massProps.com)
      // accelerazione del punto: traslazione + angolare + centripeta
      const a = add(accelerazione, add(cross(alfa, d), cross(omega, cross(omega, d))))
      const risultante = sub(perParte.get(p.id) ?? ZERO, scale(a, massa))
      momento = add(momento, cross(sub(p.position, g.position), risultante))
    }
    out.set(g.id, momento)
  }
  return out
}

export interface Flessione {
  /** angolo di flessione per giunto, come vettore: direzione = asse, modulo = angolo */
  readonly angoli: Record<string, Vec3>
}

export const flessioneNulla = (): Flessione => ({ angoli: {} })

/**
 * Aggiorna la deformazione.
 *
 * E QUASI STATICA con un ritardo del primo ordine: l'angolo insegue il valore
 * d'equilibrio M/k invece di oscillargli attorno. Cosi non ci sono frequenze
 * proprie, quindi non c'e niente che possa risuonare, quindi il razzo non
 * diventa uno spaghetto — che e il difetto piu famoso del genere.
 */
export function aggiornaFlessione(
  f: Flessione, momenti: Map<string, Vec3>, giunti: readonly Giunto[], dt: number,
): Flessione {
  const fattore = 1 - Math.exp(-Math.max(0, dt) / TAU)
  const angoli: Record<string, Vec3> = {}
  for (const g of giunti) {
    const m = momenti.get(g.id) ?? ZERO
    const equilibrio = scale(m, 1 / g.stiffness)
    const attuale = f.angoli[g.id] ?? ZERO
    angoli[g.id] = add(attuale, scale(sub(equilibrio, attuale), fattore))
  }
  return { angoli }
}

/** Il primo giunto che cede, se ce n'e uno. */
export function giuntoRotto(momenti: Map<string, Vec3>, giunti: readonly Giunto[]): string | null {
  let peggiore: string | null = null
  let eccesso = 1
  for (const g of giunti) {
    const rapporto = length(momenti.get(g.id) ?? ZERO) / g.maxMoment
    if (rapporto > eccesso) { eccesso = rapporto; peggiore = g.id }
  }
  return peggiore
}

/** Quanto un giunto e vicino a rompersi, da 0 a oltre 1. */
export function utilizzoStrutturale(momenti: Map<string, Vec3>, giunti: readonly Giunto[]): number {
  let massimo = 0
  for (const g of giunti) {
    massimo = Math.max(massimo, length(momenti.get(g.id) ?? ZERO) / g.maxMoment)
  }
  return massimo
}

/**
 * Il veicolo deformato: le parti a valle di ogni giunto piegato risultano
 * ruotate. Serve al rendering e all'aerodinamica, perche una parte piegata
 * offre al vento una faccia diversa.
 */
export function applicaFlessione(vessel: Vessel, giunti: readonly Giunto[], f: Flessione): Vessel {
  let parts = vessel.parts

  // dai giunti piu vicini alla radice verso l'esterno: piegare un giunto
  // sposta tutto quello che gli sta a valle, compresi gli altri giunti, e il
  // perno di quelli va preso DOPO che i precedenti hanno gia agito
  const ordinati = [...giunti].sort(
    (a, b) => ancestorsOf(vessel, a.id).length - ancestorsOf(vessel, b.id).length,
  )

  for (const g of ordinati) {
    const ang = f.angoli[g.id]
    if (!ang) continue
    const angolo = length(ang)
    if (angolo < 1e-9) continue

    const corrente = { rootId: vessel.rootId, parts }
    const parte = parts.find((p) => p.id === g.id)
    const padre = parts.find((p) => p.id === g.parentId)
    if (!parte || !padre) continue

    const perno = scale(add(parte.position, padre.position), 0.5)
    const q = fromAxisAngle(ang, angolo)
    const daRuotare = new Set(subtreeOf(corrente, g.id).map((p) => p.id))

    parts = parts.map((p): PartInstance => {
      if (!daRuotare.has(p.id)) return p
      return {
        ...p,
        position: add(perno, rotate(q, sub(p.position, perno))),
        rotation: normalizeQuat(mulQuat(q, p.rotation)),
      }
    })
  }

  return { rootId: vessel.rootId, parts }
}
